import React, { useCallback, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { useDialogManager } from 'src/components/widgets/dialog';
import { showErrorNotification } from 'src/components/widgets/notificationToaster';
import TestOrdersPurchaseDialog from 'src/components/widgets/testPage/testOrdersPurchaseDialog';
import { backofficeUrl, T } from 'src/shared/helpers';
import { isComposerAddToCartMessage } from 'src/shared/zakekeMessage';
import styled, { css } from 'styled-components';
import { useProductConfigurationState } from '../../state';
import HeaderToolbar from '../configurator-components/headerToolbar';
import { ShoppingPreview_shoppingPreviewUrl_Query } from './__generated__/ShoppingPreview_shoppingPreviewUrl_Query.graphql';
import { ShoppingPreview_testOrder_Mutation } from './__generated__/ShoppingPreview_testOrder_Mutation.graphql';

// GraphQL query per ottenere l'URL della shopping preview
const shoppingPreviewUrlQuery = graphql`
    query ShoppingPreview_shoppingPreviewUrl_Query($input: ConfiguratorUrlInput!) {
        shoppingPreviewUrl(input: $input)
        subscription {
            isOverMaxNumTestOrders
        }
    }
`;

const testOrderMutation = graphql`
    mutation ShoppingPreview_testOrder_Mutation($compositionDocID: String) {
        createTestOrder(compositionDocID: $compositionDocID) {
            orderID
        }
    }
`;

const Container = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgb(246, 248, 249);
    z-index: 30;
    width: 100%;

    & > * {
        width: 100%;
    }
`;

const MainContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;

const MainContentLayout = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    height: 100%;
    max-height: 100%;
    background: #f6f8f9;
`;

const PreviewContainer = styled.div`
    position: relative;
    flex: 1;
    padding: 20px 12px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const LeftVerticalToolbarContainer = styled.div<{ $hidden?: boolean }>`
    padding: 0 12px 12px;
    overflow-y: auto;
    width: calc(54px + 350px);
    ${(props) =>
        props.$hidden &&
        css`
            display: none;
        `}
`;

const ShoppingPreviewIframe = styled.iframe<{ $previewSize: 'desktop' | 'mobile' | 'desktop-wide' }>`
    border: none;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    background: white;

    ${(props) => {
        switch (props.$previewSize) {
            case 'mobile':
                return css`
                    width: 375px;
                    height: 667px;
                `;
            case 'desktop-wide':
                return css`
                    width: 1440px;
                    height: 900px;
                `;
            case 'desktop':
            default:
                return css`
                    width: 1600px;
                    height: 800px;
                `;
        }
    }}
`;

const ShoppingPreview = () => {
    const [previewSize, changePreviewSize] = useState<'desktop' | 'mobile' | 'desktop-wide'>('desktop');
    const [viewMode, setViewMode] = useState<'scene-editor' | 'composer' | 'shopping-preview'>('scene-editor');
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const navigate = useNavigate();
    const { showDialog } = useDialogManager();
    const [commitTestOrder] = useMutation<ShoppingPreview_testOrder_Mutation>(testOrderMutation);
    // Otteniamo il productID dal state di configurazione del prodotto
    const { state: productConfigState } = useProductConfigurationState();

    // Costruiamo l'input per la query GraphQL
    // Il productID dovrebbe essere nel formato "guid://Zakeke/Model/{ID}"
    const rawProductId = productConfigState.product?.id;
    const productID = rawProductId ? `${rawProductId}` : null;

    // Eseguiamo la query GraphQL solo se abbiamo un productID valido
    const shoppingPreviewData = productID
        ? useLazyLoadQuery<ShoppingPreview_shoppingPreviewUrl_Query>(shoppingPreviewUrlQuery, {
              input: { productID }
          })
        : null;

    const shoppingPreviewUrl = shoppingPreviewData?.shoppingPreviewUrl;
    const openTestOrdersPurchaseDialog = useCallback(() => {
        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.delete('charge_id');
        returnUrl.searchParams.delete('forTEST_ORDERS');

        showDialog(
            'shopping-preview-test-orders-purchase',
            <TestOrdersPurchaseDialog returnUrlAfterApproval={returnUrl.toString()} onPurchased={() => window.location.reload()} />
        );
    }, [showDialog]);

    useEffect(() => {
        const handleTestOrder = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow || !isComposerAddToCartMessage(event.data)) return;

            if (shoppingPreviewData?.subscription?.isOverMaxNumTestOrders) {
                openTestOrdersPurchaseDialog();
                return;
            }

            commitTestOrder({
                variables: { compositionDocID: event.data.message.composition },
                onCompleted: ({ createTestOrder }) => {
                    if (createTestOrder?.orderID) {
                        navigate(backofficeUrl(`/order/${createTestOrder.orderID}`));
                        return;
                    }
                    showErrorNotification({ message: T._('Failed creating the test order. Please try again.', 'Admin') });
                },
                onError: () => {
                    showErrorNotification({ message: T._('Failed creating the test order. Please try again.', 'Admin') });
                }
            });
        };

        window.addEventListener('message', handleTestOrder);
        return () => window.removeEventListener('message', handleTestOrder);
    }, [commitTestOrder, navigate, openTestOrdersPurchaseDialog, shoppingPreviewData?.subscription?.isOverMaxNumTestOrders]);

    return (
        <Container>
            <HeaderToolbar activeMode='test' previewSize={previewSize} changePreviewSize={changePreviewSize} isShoppingPreview />
            <MainContentContainer>
                <MainContentLayout>
                    <PreviewContainer>
                        {productID ? (
                            shoppingPreviewUrl ? (
                                <ShoppingPreviewIframe
                                    ref={iframeRef}
                                    src={shoppingPreviewUrl}
                                    $previewSize={previewSize}
                                    title='Shopping Preview'
                                    loading='lazy'
                                    allow='camera; microphone; fullscreen'
                                />
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <p>Caricamento shopping preview in corso...</p>
                                    <p style={{ fontSize: '14px', color: '#666' }}>Product ID: {productID}</p>
                                </div>
                            )
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <p>❌ Impossibile caricare la shopping preview</p>
                                <p style={{ fontSize: '14px', color: '#666' }}>Product ID non disponibile nel state di configurazione</p>
                                <details style={{ marginTop: '20px', textAlign: 'left' }}>
                                    <summary>Debug info</summary>
                                    <pre style={{ fontSize: '12px', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                                        {JSON.stringify(productConfigState, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        )}
                    </PreviewContainer>
                </MainContentLayout>
            </MainContentContainer>
        </Container>
    );
};

export default ShoppingPreview;
